import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { invitationService, memberService } from "@/services/invitationService";
import { grantService } from "@/services/roleService";
import type {
  GrantScope,
  OrganizationAcceptPayload,
  WorkspaceInvitationAcceptPayload,
} from "@/types/invitation";

const keys = {
  user: (wsId: number) => ["invitations", "user", wsId] as const,
  org: (wsId: number) => ["invitations", "org", wsId] as const,
  client: (wsId: number) => ["invitations", "client", wsId] as const,
  workspace: () => ["invitations", "workspace"] as const,
  members: (wsId: number) => ["members", wsId] as const,
};

// ─── Público (aceite de convite / onboarding) ───────────────────────────────
export function useInvitation(token: string) {
  return useQuery({
    queryKey: ["invitation-detail", token],
    queryFn: () => invitationService.getDetail(token),
    retry: false,
  });
}

export function useAcceptWorkspaceInvitation() {
  return useMutation({
    mutationFn: ({ token, payload }: { token: string; payload: WorkspaceInvitationAcceptPayload }) =>
      invitationService.accept(token, payload),
  });
}

export function useAcceptOrganizationInvitation() {
  return useMutation({
    mutationFn: ({ token, payload }: { token: string; payload: OrganizationAcceptPayload }) =>
      invitationService.accept(token, payload),
  });
}

export function useUserInvitations(workspaceId: number | null) {
  return useQuery({
    queryKey: keys.user(workspaceId ?? 0),
    queryFn: () => invitationService.listUser(workspaceId as number),
    enabled: workspaceId != null,
  });
}

export function useMembers(workspaceId: number | null) {
  return useQuery({
    queryKey: keys.members(workspaceId ?? 0),
    queryFn: () => memberService.list(workspaceId as number),
    enabled: workspaceId != null,
  });
}

/** Invalidate every invitation/member list for a workspace after a mutation. */
export function useInvalidateInvitations(workspaceId: number) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["invitations"] });
    void qc.invalidateQueries({ queryKey: keys.members(workspaceId) });
  };
}

export function useRevokeMember(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (grantId: number) => memberService.revoke(workspaceId, grantId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members(workspaceId) }),
  });
}

export interface UpdateMemberPayload {
  role_id?: number;
  scope?: GrantScope;
  organization_ids?: number[];
  client_ids?: number[];
}

export function useUpdateMember(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ grantId, payload }: { grantId: number; payload: UpdateMemberPayload }) =>
      grantService.update(workspaceId, grantId, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.members(workspaceId) }),
  });
}

export function useRevokeUserInvitation(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => invitationService.revokeUser(workspaceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.user(workspaceId) }),
  });
}

export function useRevokeOrgInvitation(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => invitationService.revokeOrg(workspaceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.org(workspaceId) }),
  });
}

export function useRevokeClientInvitation(workspaceId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => invitationService.revokeClient(workspaceId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.client(workspaceId) }),
  });
}

export function useRevokeWorkspaceInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => invitationService.revokeWorkspace(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.workspace() }),
  });
}
